import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Copy, ExternalLink, Receipt, Server, RefreshCw } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  UniversalEmptyState,
  Card,
  Button,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  useToast,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import {
  adminPendingOrdersService,
  type AdminPendingOrderRow,
} from '@/domains/billing/services/adminPendingOrdersService';
import { formatDate, formatZAR } from '@/shared/utils/format';

/**
 * AdminPendingOrdersPage — cross-customer view of in-flight service orders.
 *
 * Powered by `v_admin_pending_orders` view. Shows every service_order whose
 * status is NOT in (complete, cancelled, failed_terminal) — i.e., the full
 * pre-completion pipeline including:
 *   - pending_payment (most common — customer hasn't paid the invoice yet)
 *   - paid (saga firing, provisioning runner picking it up)
 *   - processing / awaiting_action
 *
 * Quick actions per row:
 *   - Open invoice (admin detail)
 *   - Open customer
 *   - Copy PayFast URL to clipboard (so admin can email/whatsapp the customer)
 *
 * Built 2026-06-02 after the admin add-hosting modal shipped — gives a
 * single surface for tracking pending orders that the AdminInvoicesPage
 * doesn't surface cleanly (invoice list shows the financial side; this
 * shows the order pipeline).
 */

/** '—' for an absent amount; the FORMATTING is the shared helper's job.
 *  Named so it cannot be mistaken for another local currency formatter. */
function zarOrDash(n: number | null): string {
  return n === null ? '—' : formatZAR(n);
}

const ORDER_STATUS_TONE: Record<string, StatusTone> = {
  pending_payment: 'pending',
  paid: 'info',
  processing: 'info',
  failed_retryable: 'overdue',
  awaiting_action: 'overdue',
};

const INVOICE_STATE_TONE: Record<string, StatusTone> = {
  draft: 'draft',
  unpaid: 'pending',
  grace: 'expiring',
  paid: 'active',
  suspended: 'overdue',
  refunded: 'info',
  cancelled: 'draft',
};

const SKU_LABEL: Record<string, string> = {
  hosting: 'Hosting',
  'studio-starter': 'Studio Starter',
  'care-pro': 'Care plan',
  'email-setup': 'Email setup',
};

function daysAgo(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

type StatusFilter = 'all' | 'pending_payment' | 'paid_processing' | 'stuck';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All in-flight' },
  { value: 'pending_payment', label: 'Awaiting customer payment' },
  { value: 'paid_processing', label: 'Paid · provisioning' },
  { value: 'stuck', label: 'Stuck / retryable' },
];

export default function AdminPendingOrdersPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<AdminPendingOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  function refresh() {
    setLoading(true);
    setError(null);
    adminPendingOrdersService
      .listAll()
      .then((data) => {
        setRows(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
  }

  useEffect(() => {
    refresh();
  }, []);

  /** FLOW — orders are events. `Created` on `order_created_at`. */
  const table = useTableFilters<AdminPendingOrderRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Created',
    dateOf: (r) => r.order_created_at,
    searchText: (r) =>
      [
        r.sku_code,
        r.customer_name ?? '',
        r.customer_email ?? '',
        r.customer_company ?? '',
        r.invoice_number ?? '',
      ].join(' '),
    facets: useMemo(
      () => [
        (r: AdminPendingOrderRow) => {
          if (statusFilter === 'pending_payment') return r.order_status === 'pending_payment';
          if (statusFilter === 'paid_processing')
            return r.order_status === 'paid' || r.order_status === 'processing';
          if (statusFilter === 'stuck')
            return (
              r.order_status === 'failed_retryable' ||
              r.order_status === 'awaiting_action' ||
              (r.order_status === 'pending_payment' && daysAgo(r.order_created_at) > 7)
            );
          return true;
        },
      ],
      [statusFilter],
    ),
  });

  const filteredRows = table.rows;

  async function copyPayUrl(row: AdminPendingOrderRow) {
    if (!row.invoice_id) {
      toast({ tone: 'warning', title: 'No invoice', body: 'This order has no linked invoice yet.' });
      return;
    }
    const url = adminPendingOrdersService.buildPayUrl(row.invoice_id, row.invoice_payment_token);
    try {
      await navigator.clipboard.writeText(url);
      toast({
        tone: 'success',
        title: 'PayFast link copied',
        body: `Paste to ${row.customer_name ?? row.customer_email ?? 'the customer'} via email / WhatsApp.`,
      });
    } catch {
      toast({ tone: 'danger', title: 'Copy failed', body: url });
    }
  }

  const columns = useMemo<UniversalTableColumn<AdminPendingOrderRow>[]>(
    () => [
      {
        key: 'customer',
        header: 'Customer',
        render: (r) => (
          <Link
            to={`/admin/customers/${r.customer_id}`}
            className="font-medium text-primary-ink hover:underline"
          >
            <div>{r.customer_name ?? '—'}</div>
            <div className="text-xs text-muted-foreground">
              {r.customer_email ?? r.customer_id.slice(0, 8)}
              {r.customer_company && <> · {r.customer_company}</>}
            </div>
          </Link>
        ),
      },
      {
        key: 'sku',
        header: 'Product',
        render: (r) => (
          <div className="text-sm">
            <div className="font-medium">{SKU_LABEL[r.sku_code] ?? r.sku_code}</div>
            {r.plan_name && <div className="text-xs text-muted-foreground">{r.plan_name}</div>}
          </div>
        ),
      },
      {
        key: 'fqdn',
        header: 'Domain',
        render: (r) => (
          <div className="text-sm">
            {r.primary_fqdn ? (
              <code className="rounded bg-muted px-1 text-xs">{r.primary_fqdn}</code>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
            {r.fqdn_mode && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {r.fqdn_mode === 'register' && r.bundled_domain ? `registering ${r.bundled_domain}` : r.fqdn_mode}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'invoice',
        header: 'Invoice',
        render: (r) =>
          r.invoice_id ? (
            <Link to={`/admin/billing`} className="text-primary-ink hover:underline text-sm font-medium">
              {r.invoice_number ?? r.invoice_id.slice(0, 8)}
            </Link>
          ) : (
            <span className="text-xs text-muted-foreground">no invoice</span>
          ),
      },
      {
        key: 'total',
        header: 'Total',
        render: (r) => (
          <span className="text-sm font-medium tabular-nums">{zarOrDash(r.invoice_total)}</span>
        ),
      },
      {
        key: 'order_status',
        header: 'Order',
        render: (r) => (
          <StatusBadge tone={ORDER_STATUS_TONE[r.order_status] ?? 'draft'}>
            {r.order_status.replace(/_/g, ' ')}
          </StatusBadge>
        ),
      },
      {
        key: 'invoice_state',
        header: 'Invoice state',
        render: (r) =>
          r.invoice_state ? (
            <StatusBadge tone={INVOICE_STATE_TONE[r.invoice_state] ?? 'draft'}>
              {r.invoice_state}
            </StatusBadge>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        key: 'created',
        header: 'Created',
        render: (r) => (
          <div className="text-xs">
            <div>{formatDate(r.order_created_at)}</div>
            <div className="text-muted-foreground">{daysAgo(r.order_created_at)}d ago</div>
          </div>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        render: (r) => (
          <div className="flex items-center gap-1">
            {r.invoice_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyPayUrl(r)}
                title="Copy customer PayFast link to clipboard"
              >
                <Copy className="h-3.5 w-3.5" />
                Pay link
              </Button>
            )}
            <Link
              to={`/admin/customers/${r.customer_id}/billing`}
              title="Open customer billing tab"
            >
              <Button variant="outline" size="sm">
                <ExternalLink className="h-3.5 w-3.5" />
                Open
              </Button>
            </Link>
          </div>
        ),
      },
    ],
    // Deliberate: this effect is meant to run on the listed deps only. (Was an
    // eslint-disable for react-hooks/exhaustive-deps; this config does not
    // register that plugin, so the directive was dangling.)
    []
  );

  return (
    <UniversalPage
      title="Pending orders"
      description="In-flight service orders across customers — awaiting payment, processing, or stuck."
      actions={
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      }
    >
      <Card interactive={false} className="p-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-muted-foreground">Filter:</span>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              className={
                statusFilter === f.value
                  ? 'rounded border border-primary bg-primary/10 px-3 py-1 text-primary'
                  : 'rounded border border-border bg-background px-3 py-1 text-muted-foreground hover:text-foreground'
              }
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-muted-foreground">
            {filteredRows.length} of {rows.length}
          </span>
        </div>
      </Card>

      {error && (
        <NotificationCard
          tone="danger"
          title="Could not load pending orders"
          body={error.message}
        />
      )}

      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : filteredRows.length === 0 ? (
        <Card interactive={false}>
          <UniversalEmptyState
            icon={Receipt}
            title="No pending orders"
            description={
              statusFilter === 'all'
                ? 'Every order has either completed, been cancelled, or failed terminally. Anyone placing a new hosting order via /admin/customers/:id Hosting → Add hosting will appear here while their invoice is unpaid.'
                : `No orders match the "${STATUS_FILTERS.find((f) => f.value === statusFilter)?.label}" filter. Switch back to "All in-flight" to see everything.`
            }
            action={
              <Link to="/admin/customers">
                <Button variant="outline">
                  <Server className="h-4 w-4" />
                  Browse customers
                </Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Created"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search product, customer, invoice"
            className="ml-auto w-full max-w-xs"
          />
        </div>
        <UniversalTable
          columns={columns}
          data={filteredRows}
          caption={table.caption}
          getRowKey={(r) => r.service_order_id}
        />
        </>
      )}
    </UniversalPage>
  );
}
